import React, {
  FunctionComponent,
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";
import sepReasons from "./sepReasons.json";
import { EnrollmentFormProps2023 } from "../../EnrollmentFormPage";
import PlanInfo from "../../PlanInfo";
import BackNextButtons from "../../BackNextButtons";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
} from "../../../../helpers";
import {
  CollapsibleBox,
  FocusableHeader,
  ScrollTable,
  TooltipWithClose,
} from "../../../../components";
import {
  Choice,
  TextField,
  AlertCircleIcon,
  Alert,
} from "@cmsgov/ds-medicare-gov";
import { withRouter, RouteComponentProps } from "react-router";
import routes from "../../../../app/routes";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import {
  linkToJoiningAPlan,
  linkToSpecialEnrollmentPeriodInfo,
} from "../../../../helpers/CONSTANTS";
import { AppContext } from "../../../../app/store";
import { SpecialEnrollmentChoice } from "./SpecialEnrollmentChoice";

const SepEnrollments: FunctionComponent<EnrollmentFormProps2023 &
  RouteComponentProps> = ({
  enrollData,
  setEnrollData,
  nextPage,
  plan,
  inReview,
  history,
}) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);

  const [formSubmittedCount, setFormSubmittedCount] = useState(0);
  const [formIsValid, setFormIsValid] = useState(false);
  const [reasonsValid, setReasonsValid] = useState(false);
  const [otherReasonValid, setOtherReasonValid] = useState(true);
  const [enrollmentTableExpanded, setEnrollmentTableExpanded] = useState(false);
  const [alertFormError, setAlertFormError] = useState(false);

  const formSubmitted = formSubmittedCount > 0;

  const alertDiv = useRef<HTMLDivElement>(null);

  const {
    initialEnrollment: ieReasons,
    specialEnrollment: seReasons,
    medicareAdvantagePlan: maReasons,
  } = sepReasons;

  const next = (): void => {
    const {
      isValid,
      sepOtherReasonValidity,
      sepReasonsValidity,
    } = getValidationObject(enrollData, plan, EnrollmentPages.Sep);

    setReasonsValid(!!sepReasonsValidity);
    setOtherReasonValid(!!sepOtherReasonValidity);
    setFormIsValid(isValid);
    setFormSubmittedCount(formSubmittedCount + 1);
    // Hides or unhides error messages and the main error alert. If there are
    // errors, screen-reader will re-read. See the `BackNextButtons` `nextPage()` callback
    setAlertFormError(!isValid);

    if (isValid) {
      nextPage();
    }
  };

  useEffect(() => {
    if (formSubmitted && !formIsValid) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [formSubmittedCount]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enoll_form.start_your_application")}
      />

      <div>
        <PlanInfo plan={plan} showRequiredFieldNote={false} />

        <p>
          <FormattedHTMLMessage id="enroll_form.enrollment_periods" />
        </p>

        <p>{t("enroll_form.cost_plan_enrollment_period")}</p>

        <CollapsibleBox
          label={
            enrollmentTableExpanded
              ? t("enroll_form.hide_expanded_enrollment_periods")
              : t("enroll_form.show_expanded_enrollment_periods")
          }
          onOpening={(): void => setEnrollmentTableExpanded(true)}
          onClosing={(): void => setEnrollmentTableExpanded(false)}
          titleClassname="EnrollmentFormPage__expandButton"
          caretClassname="EnrollmentFormPage__expandCaret"
        >
          <ScrollTable className="EnrollmentFormPage__sepTable">
            <thead>
              <tr>
                <th>{t("sep.2021.enrollment_period")}</th>
                <th>{t("sep.2021.you_can")}</th>
                <th>{t("sep.2021.coverage_starts")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-text-heading--md">
                    {t("sep.2021.open_enrollment_period")}
                  </p>
                  <p className="ds-u-margin-y--0">{t("sep.2021.oct15_dec7")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.join_drop_switch")}</span>
                </td>
                <td>
                  <span>{t("sep.2021.jan1_next_year")}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-text-heading--md">
                    {t("sep.2021.iep")}
                  </p>
                  <p className="ds-u-margin-y--0">
                    {t("sep.2021.new_to_medicare")}
                  </p>
                  <p>{t("sep.2021.starts_3_months_before_medicare")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.iep.join_any_plan")}</span>
                  <ul className="ds-u-margin-y--1">
                    <li className="ds-u-margin-bottom--1">
                      {t("sep.2021.iep.join_part_a_part_b")}
                    </li>
                    <li>{t("sep.2021.iep.join_drug_plan")}</li>
                  </ul>
                </td>
                <td>
                  <span>{t("sep.2021.iep.varies")}</span>
                  <ul className="ds-u-margin-y--1">
                    <li className="ds-u-margin-bottom--1">
                      <FormattedHTMLMessage id="sep.2021.iep.request_before" />
                    </li>
                    <li>
                      <FormattedHTMLMessage id="sep.2021.iep.request_after" />
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-text-heading--md">
                    {t("sep.2021.iep_new")}
                  </p>
                  <p className="ds-u-margin-y--0">
                    {t("sep.2021.iep_new.only")}
                  </p>
                  <p>{t("sep.2021.iep_new.three_moths")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.iep_new.join")}</span>
                </td>
                <td>
                  <span>{t("sep.2021.iep_new.same_day")}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-text-heading--md">
                    {t("sep.2021.mapd")}
                  </p>
                  <p className="ds-u-margin-y--0">{t("sep.2021.mapd.only")}</p>
                  <ul className="ds-u-margin-y--1">
                    <li className="ds-u-margin-bottom--1">
                      {t("sep.2021.mapd.jan1_march31")}
                    </li>
                    <li>
                      <TooltipWithClose
                        headerText={t("sep.2021.mapd.first_three_months.title")}
                        title={t("sep.2021.mapd.first_three_months.text")}
                      >
                        {t("sep.2021.mapd.first_three_months")}
                      </TooltipWithClose>
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="ds-u-margin-y--0">
                    <li className="ds-u-margin-bottom--1">
                      {t("sep.2021.mapd.switch")}
                    </li>
                    <li>
                      <FormattedMessage
                        id="sep.2021.mapd.drop"
                        values={{
                          joinADrugPlan: (
                            <TooltipWithClose
                              headerText={t(
                                "sep.2021.mapd.join_drug_plan.title"
                              )}
                              title={
                                <FormattedHTMLMessage id="sep.2021.mapd.join_drug_plan.text" />
                              }
                            >
                              <FormattedHTMLMessage id="sep.2021.mapd.join_drug_plan" />
                            </TooltipWithClose>
                          ),
                        }}
                      />
                    </li>
                  </ul>
                </td>
                <td>
                  <span>{t("sep.2021.mapd.first_of_month")}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-text-heading--md">
                    {t("sep.2021.sep")}
                  </p>
                  <p>{t("sep.2021.sep.certain_situations")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.sep.join_switch")}</span>
                </td>
                <td>
                  <span>{t("sep.2021.sep.first_of_month")}</span>
                </td>
              </tr>
            </tbody>
          </ScrollTable>
          <p>
            <a
              href={linkToJoiningAPlan(state.language)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t("sep.2021.learn_more_enrollment_periods")}
            </a>
          </p>
        </CollapsibleBox>

        <div className="EnrollmentFormPage__Sep2021">
          <h2 className="EnrollmentFormPage__Sep2021-header EnrollmentFormPage__Sep2021-header--confirm-enrollment-period ds-u-margin-bottom--0">
            {t("sep_form.confirm_enroll_period")}
          </h2>
          <p className="ds-u-margin-top--1">
            {t("sep.2021.only_join_if_situation_matches")}
          </p>
          <p className="ds-u-margin-top--1">
            <FormattedHTMLMessage id="sep.2021.eligibility_note" />
          </p>

          <div ref={alertDiv}>
            {!formIsValid && formSubmitted && (
              <Alert
                className="ds-u-margin-top--2"
                heading={t("enroll_form.needs_your_attention")}
                variation="error"
              >
                {t("enroll_form.complete_required")}
              </Alert>
            )}
          </div>

          <p className="ds-u-margin-bottom--0 ds-u-margin-top--7">
            {t("sep.2021.check_all_that_apply")}
          </p>

          {!reasonsValid && formSubmitted && alertFormError && (
            <p className="ds-u-color--error ds-u-margin-bottom--0" role="alert">
              <AlertCircleIcon className="ds-u-margin-right--2" />
              {t("sep_form.select_at_least_one")}
            </p>
          )}

          {/* Initial Enrollment Period & other new to Medicare situations */}
          <fieldset className="ds-u-border-bottom--1 ds-u-padding-bottom--2 ds-u-margin-top--1">
            <legend className="ds-text-heading--xl ds-u-margin-top--1">
              {t("sep.2021.initial_enrollment_period")}
            </legend>
            {ieReasons.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
                plan={plan}
                alertFormError={alertFormError}
              />
            ))}
          </fieldset>

          {/* Medicare Advantage Plan Open Enrollment Period */}
          <h3 className="ds-text-heading--xl">
            {t("sep.2021.mapd_enrollment_period")}
          </h3>
          <div className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
            {maReasons.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
                plan={plan}
                alertFormError={alertFormError}
              />
            ))}
          </div>

          {/* Special Enrollment Period */}
          <h3 className="ds-text-heading--xl">
            {t("sep_form.special_enrollment_period")}
          </h3>
          <a
            href={linkToSpecialEnrollmentPeriodInfo(state.language)}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("sep.2021.learn_more_sep")}
          </a>

          {/* Special Enrollment Period - You change where you live */}
          <fieldset className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            <legend className="ds-text-heading--lg ds-u-margin-bottom--0">
              {t("sep_form.change_where_you_live")}
            </legend>
            <p className="ds-u-margin-top--1">
              {t("sep.2021.after_move_join_plan")}
            </p>
            {seReasons.changeLocation.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
                plan={plan}
                alertFormError={alertFormError}
              />
            ))}
          </fieldset>

          {/* Special Enrollment Period - You lose or have a change in your current coverage */}
          <fieldset className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            <legend className="ds-text-heading--lg ds-u-margin-bottom--0">
              {t("sep_form.you_lose_coverage")}
            </legend>
            <p className="ds-u-margin-top--1">
              {t("sep.2021.two_months_current_coverage")}
            </p>
            {sepReasons.specialEnrollment.loseCoverage.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
                plan={plan}
                alertFormError={alertFormError}
              />
            ))}
          </fieldset>

          {/* Special Enrollment Period - You have Medicare and Medicaid, or you get help paying your drug costs */}
          <fieldset className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            <legend className="ds-text-heading--lg ds-u-margin-bottom--0">
              {t("sep.2021.medicare_and_medicaid")}
            </legend>
            <p className="ds-u-margin-top--1">
              {t("sep.2021.two_months_medicaid")}
            </p>
            {sepReasons.specialEnrollment.medicareMedicaid.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
                plan={plan}
                alertFormError={alertFormError}
              />
            ))}
          </fieldset>

          {/* Special Enrollment Period - Other special situations */}
          <fieldset className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            <legend className="ds-text-heading--lg ds-u-margin-bottom--0">
              {t("sep_form.special_situations")}
            </legend>
            {seReasons.otherSpecialSituations.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
                plan={plan}
                alertFormError={alertFormError}
              />
            ))}
          </fieldset>
          {/* Special Enrollment Period - CSRs only */}
          {state.csr && (
            <fieldset className="ds-u-margin-bottom--0 ds-u-margin-top--4">
              <legend className="ds-text-heading--lg ds-u-margin-bottom--0">
                {t("sep_form.csrs_only")}
              </legend>
              <Choice
                type="checkbox"
                name={t("sep_reason.other")}
                value={"OTH"}
                checked={enrollData.hasSepOtherReason}
                onChange={(): void => {
                  const otherLabel = "sep_reason.other";

                  const index = enrollData.sepReasons.findIndex(
                    r => r.key === otherLabel
                  );

                  if (index >= 0) {
                    setEnrollData({
                      ...enrollData,
                      sepReasons: enrollData.sepReasons.filter(
                        r => r.key !== otherLabel
                      ),
                      hasSepOtherReason: false,
                      sepOtherReason: "",
                    });
                  } else {
                    setEnrollData({
                      ...enrollData,
                      sepReasons: [
                        ...enrollData.sepReasons,
                        {
                          code: "OTH",
                          key: otherLabel,
                          requiresDate: false,
                        },
                      ],
                      hasSepOtherReason: true,
                    });
                  }
                }}
                checkedChildren={
                  <div className="ds-u-margin-left--7">
                    <TextField
                      errorMessage={
                        !otherReasonValid && t("sep_form.enter_a_reason")
                      }
                      name={t("sep_form.other_reason")}
                      label={t("sep_form.other_reason")}
                      onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        setEnrollData({
                          ...enrollData,
                          sepOtherReason: e.currentTarget.value,
                        });
                      }}
                    />
                  </div>
                }
              >
                {t("sep_reason.other")}
              </Choice>
              {seReasons.csr.map((reason, i) => (
                <SpecialEnrollmentChoice
                  key={i}
                  reason={reason}
                  setEnrollData={setEnrollData}
                  enrollData={enrollData}
                  formSubmitted={formSubmitted}
                  plan={plan}
                  alertFormError={alertFormError}
                />
              ))}
            </fieldset>
          )}

          {/* Can’t find a statement to match your situation? */}
          <h4 className="ds-text-heading--lg ds-u-margin-top--7">
            {t("sep_form.nothing_matches_situation")}
          </h4>
          <p className="ds-u-margin-top--1">{t("sep.2021.contact_plan")}</p>
        </div>

        <BackNextButtons
          nextPage={() => {
            // Force errors and error alert to hide momentarily before re-checking
            // form validity. This will re-announce error states to screen-readers
            setAlertFormError(false);
            setTimeout(next, 100);
          }}
          previousPage={(): void => {
            history.push(routes.searchResults);
          }}
          inReview={inReview}
        />
      </div>
    </div>
  );
};

export default withRouter(SepEnrollments);
